import { useEffect, useMemo, useState } from "react";
import { ChevronDown, List } from "lucide-react";

export type TocItem = { id: string; text: string; level: number };

// Match GitHub's rehype-slug behaviour closely enough for H2 anchors.
function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

export function extractTocFromMarkdown(md: string): TocItem[] {
  const items: TocItem[] = [];
  const lines = md.split("\n");
  let inCode = false;
  for (const line of lines) {
    if (/^```/.test(line)) {
      inCode = !inCode;
      continue;
    }
    if (inCode) continue;
    const m = /^(#{2,3})\s+(.+?)\s*#*\s*$/.exec(line);
    if (!m) continue;
    const level = m[1].length;
    const text = m[2].replace(/[*_`]/g, "").trim();
    items.push({ id: slugify(text), text, level });
  }
  return items;
}

export function TableOfContents({ markdown }: { markdown: string }) {
  const items = useMemo(() => extractTocFromMarkdown(markdown).filter((i) => i.level === 2), [markdown]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (items.length === 0) return;
    const headings = items
      .map((i) => document.getElementById(i.id))
      .filter((el): el is HTMLElement => !!el);
    if (headings.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
    );
    headings.forEach((h) => observer.observe(h));
    return () => observer.disconnect();
  }, [items]);

  if (items.length < 3) return null;

  return (
    <>
      {/* Mobile: collapsible */}
      <div className="lg:hidden">
        <div className="rounded-2xl border border-border bg-card/50 backdrop-blur">
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="flex w-full items-center justify-between gap-2 px-5 py-4 text-start"
            aria-expanded={mobileOpen}
          >
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
              <List className="h-4 w-4" /> On this page
            </span>
            <ChevronDown
              className={`h-4 w-4 text-muted-foreground transition-transform ${mobileOpen ? "rotate-180" : ""}`}
            />
          </button>
          {mobileOpen && (
            <ol className="space-y-1 px-5 pb-5 text-sm">
              {items.map((it, idx) => (
                <li key={it.id}>
                  <a
                    href={`#${it.id}`}
                    onClick={() => setMobileOpen(false)}
                    className="block rounded-md px-2 py-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <span className="me-2 text-xs text-muted-foreground/70">{String(idx + 1).padStart(2, "0")}</span>
                    {it.text}
                  </a>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>

      {/* Desktop: sticky sidebar */}
      <nav aria-label="Table of contents" className="hidden lg:block">
        <div className="sticky top-24">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            On this page
          </p>
          <ol className="space-y-1 border-s border-border">
            {items.map((it) => {
              const active = activeId === it.id;
              return (
                <li key={it.id}>
                  <a
                    href={`#${it.id}`}
                    className={`-ms-px block border-s-2 py-1.5 ps-4 text-sm transition-colors ${
                      active
                        ? "border-primary text-foreground"
                        : "border-transparent text-muted-foreground hover:border-border hover:text-foreground"
                    }`}
                  >
                    {it.text}
                  </a>
                </li>
              );
            })}
          </ol>
        </div>
      </nav>
    </>
  );
}
